import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { atsStore, automationStore, lookupStore, rappidStore } from "stores";
import stepUpdateRecordIcon from "assets/img/automation/step-updaterecord.svg";
import SubmitButton from "./SubmitButton";
import { RappidNodeType, RappidText } from "helpers/Constants";
import Select from 'react-select';
import GroupedPicker from "./GroupedPicker";
import { buildUpdateFieldGroups } from "./pickerGroups/index";
import { FormGroup, Input, Label } from "reactstrap";
import { Enum } from "helpers";
import { listCountry } from "assets/json/countries"
import HeaderForm from "./HeaderForm";
import { toJS } from "mobx";
import { EditRappidNode } from "components/Rappid/RappidCanvasFunctions";
import { TriggerType, UpdateRecordNoteType, UpdateRecordOwnerType, CustomFieldsAction, CustomFieldsActions, CandidateSkillsActions, CustomFieldDateMode, CustomFieldDateModes } from "helpers/enum";
import { validateNodeAndResetBorder } from "components/Rappid/RappidCanvasFunctions";
import CandidateSkillsSelector from "./Trigger/CandidateSkillsSelector";
import { resolveProviderLabels, getProviderUnsubscribedLabel } from "./helpers/TriggerFormHelper";
import { getUpdateRecordOptions, getCustomFieldsForTriggerType as resolveCustomFieldsForTriggerType } from "./helpers/UpdateRecordFormHelper";
import { setUpdateRecruiterNodeTextLine1, setUpdateRecruiterNodeTextLine2 } from "./UpdateRecruiterForm";
import StyledMultiSelectWithSearch from "../../common/StyledMultiSelectWithSearch";

// Stored value stays 0-10 (the BE contract), but JobAdder displays this as 5 stars in half-star
// steps, so the picker and node summary speak in stars to match what users see there.
const formatApplicationRating = (value) => `${value / 2} ${value === 2 ? 'star' : 'stars'}`;
const applicationRatingOptions = Array.from({ length: 11 }, (_, value) => ({ value, label: formatApplicationRating(value) }));

const UpdateRecordForm = observer(() => {
  const { isJobScopeDisabled, isUseLegacyAutomation } = atsStore;
  const { formUpdateRecord, isCopiedTemplate, automationType, triggerList, triggerType, automationState } = automationStore;
  const { currentWorkTypes, candidateStatuses, candidateNoteStatuses, clientStatuses, clientNoteStatuses, applicationStatuses, candidateCustomFields, clientCustomFields, placementCustomFields, jobCustomFields, applicationCustomFields, candidateSkillsHierarchy, ratings, placementStatuses, jobStatuses, countries } = lookupStore;
  const isPlacementOrJobBasedTrigger = [Enum.TriggerType.PlacementBased, Enum.TriggerType.JobBased].includes(triggerType);
  const isPlacementBasedTrigger = [Enum.TriggerType.PlacementBased].includes(triggerType);
  const optionStatusAllSelected = { statusId: 0, name: 'Any' };
  const noteStatuses = automationType === Enum.AutomationType.Candidate ? candidateNoteStatuses.filter(x => x.name !== optionStatusAllSelected.name) : clientNoteStatuses.filter(x => x.name !== optionStatusAllSelected.name);
  const optionsUpdateRecord = automationType === Enum.AutomationType.Candidate ? Enum.OwnerTypeCandidate : Enum.OwnerTypeClient;
  const ownerDefaultValue = isPlacementOrJobBasedTrigger ? (formUpdateRecord.additionalNoteType === Enum.UpdateRecordNoteType.CandidateNote ? optionsUpdateRecord[2] : optionsUpdateRecord[1]) : optionsUpdateRecord[0];
  const ownerUnsetValue = optionsUpdateRecord.find(x => x.value === Enum.UpdateRecordOwnerType.AdminUser);
  const statuses = automationType === Enum.AutomationType.Candidate ? candidateStatuses.filter(x => x.name !== optionStatusAllSelected.name) : clientStatuses.filter(x => x.name !== optionStatusAllSelected.name); //remove unused option 8372
  const applicationStatusOptions = (applicationStatuses || []).filter(
    x => x.name !== optionStatusAllSelected.name
  );
  const placementStatusOptions = (placementStatuses || []).filter(
    x => x.name !== optionStatusAllSelected.name
  );
  const jobStatusOptions = (jobStatuses || []).filter(
    x => x.name !== optionStatusAllSelected.name
  );
  const [updateRecordOption, setUpdateRecordOption] = useState(optionsUpdateRecord);
  // Until atsStore.getCompanyInfo() resolves, providerName is unknown - treat as Bullhorn (conservative)
  // so JobAdder-only options like Application rating don't render as selectable before we actually know
  // the provider, which would let a Bullhorn company pick a field that fails at automation run time.
  const [isCompanyInfoLoaded, setIsCompanyInfoLoaded] = useState(false);
  const isDraft = automationState === Enum.AutomationStateType.Draft;
  const getCustomFieldsForTriggerType = () => resolveCustomFieldsForTriggerType({
    triggerType,
    automationType,
    recordField: formUpdateRecord.recordField,
    lookups: { candidateCustomFields, clientCustomFields, placementCustomFields, jobCustomFields, applicationCustomFields },
  });
  const isCustomFieldsRecordField = (recordField) => [
    Enum.UpdateRecordList.CustomFields,
    Enum.UpdateRecordList.PlacementCustomFields,
    Enum.UpdateRecordList.ApplicationCustomFields,
    Enum.UpdateRecordList.JobCustomFields,
  ].includes(recordField);
  // Job Custom Fields and Application Custom Fields are merged into one picker list, so their fieldIds can
  // collide (they're independent datasets). Match on fieldId + updateRecordType together, never fieldId alone.
  const findCustomField = (options, fieldId, recordField) =>
    options?.find(x => x.fieldId === fieldId && x.updateRecordType === recordField) || null;
  const optionCustomFields = getCustomFieldsForTriggerType();
  const parseOptionCustomFields = optionCustomFields?.map(field => ({ ...field, values: JSON.parse(field?.values) }))

  const updateRecordOptions = resolveProviderLabels(getUpdateRecordOptions(triggerType, automationType), atsStore.providerName);
  const unsubscribeOptions = [
    { id: Enum.UnsubscribeSubType.Provider, name: getProviderUnsubscribedLabel(atsStore.providerName) },
    { id: Enum.UnsubscribeSubType.RoiAi, name: "Roi-AI Unsubscribed" }
  ];

  const isShowPlacementAdditionalNotes = () => {
    if (automationType === Enum.AutomationType.Candidate) {
      return triggerList.some(item => [
        Enum.TriggerList.CandidatePlacementStartDate,
        Enum.TriggerList.CandidatePlacementEndDate,
        Enum.TriggerList.CandidatePlacementCreatedDate,
        Enum.TriggerList.PlacementJobApplicationReceived
      ].includes(item.firstColumn.id));
    } else {
      return triggerList.some(item => [
        Enum.TriggerListClient.ClientPlacementCreatedDate,
        Enum.TriggerListClient.ClientPlacementStartDate,
        Enum.TriggerListClient.ClientPlacementEndDate
      ].includes(item.firstColumn.id));
    }
  }

  const isShowApplicationAdditionalNotes = () => {
    if (atsStore.providerName === Enum.AtsProvider.Bullhorn) {
      return false;
    }

    if (automationType === Enum.AutomationType.Candidate) {
      return triggerList.some(item => [
        Enum.TriggerList.CandidatePlacementStartDate,
        Enum.TriggerList.CandidatePlacementEndDate,
        Enum.TriggerList.CandidatePlacementCreatedDate,
        Enum.TriggerList.ApplicationStatus,
        Enum.TriggerList.JobStatus,
        Enum.TriggerList.JobJobApplicationReceived,
        Enum.TriggerList.PlacementJobApplicationReceived,
        Enum.TriggerList.InterviewDate
      ].includes(item.firstColumn.id));
    } else {
      return triggerList.some(item => [].includes(item.firstColumn.id));
    }
  }

  const isShowJobAdditionalNotes = () => {
    if (automationType === Enum.AutomationType.Candidate) {
      return triggerList.some(item => [
        Enum.TriggerList.CandidatePlacementStartDate,
        Enum.TriggerList.CandidatePlacementEndDate,
        Enum.TriggerList.CandidatePlacementCreatedDate,
        Enum.TriggerList.ApplicationStatus,
        Enum.TriggerList.JobStatus,
        Enum.TriggerList.JobJobApplicationReceived,
        Enum.TriggerList.PlacementJobApplicationReceived,
        Enum.TriggerList.InterviewDate
      ].includes(item.firstColumn.id));
    } else {
      return triggerList.some(item => [
        Enum.TriggerListClient.ClientPlacementCreatedDate,
        Enum.TriggerListClient.ClientPlacementStartDate,
        Enum.TriggerListClient.ClientPlacementEndDate,
        Enum.TriggerListClient.JobStatus,
        Enum.TriggerListClient.ClientJobInterviewDate
      ].includes(item.firstColumn.id));
    }
  }

  useEffect(() => {
    const targetElement = rappidStore.activeElement;
    const initialFormUpdateRecord = { ...formUpdateRecord };
    const isEditingExistingUpdateRecord = targetElement?.attributes?.roiNodeType === RappidNodeType.UpdateRecord;
    Promise.all([
      atsStore.getCompanyInfo(),
      automationType === Enum.AutomationType.Candidate ? lookupStore.getCandidateNoteStatuses() : lookupStore.getClientNoteStatuses(),
      automationType === Enum.AutomationType.Candidate ? lookupStore.getCandidateStatuses() : lookupStore.getClientStatuses(),
      automationType === Enum.AutomationType.Candidate ? lookupStore.getApplicationStatusesList() : null,
      automationType === Enum.AutomationType.Candidate ? lookupStore.getCandidateCustomFields() : lookupStore.getClientCustomFields(),
      // getCustomFieldsForTriggerType() below reads these once this promise resolves — gated the same way
      // it is, so we still avoid the round trip on Candidate-/Client-based automations that never read them.
      triggerType === Enum.TriggerType.PlacementBased ? lookupStore.getPlacementCustomFields() : null,
      (triggerType === Enum.TriggerType.JobBased || (triggerType === Enum.TriggerType.PlacementBased && automationType === Enum.AutomationType.Candidate))
        ? lookupStore.getJobCustomFields() : null,
      (triggerType === Enum.TriggerType.JobBased && automationType === Enum.AutomationType.Candidate) ? lookupStore.getApplicationCustomFields() : null,
      // Rating and the synced Country list are candidate-entity fields, so only candidate automations need them.
      automationType === Enum.AutomationType.Candidate ? lookupStore.getRatings() : null,
      automationType === Enum.AutomationType.Candidate ? lookupStore.getCountries() : null,
      triggerType === Enum.TriggerType.PlacementBased ? lookupStore.getPlacementStatusesList() : null,
      (triggerType === Enum.TriggerType.JobBased || triggerType === Enum.TriggerType.PlacementBased) ? lookupStore.getJobStatusesList() : null
    ]).then(() => {
      // atsStore.providerName is company-wide, not tied to this node - mark it loaded even if this
      // particular node went stale while the fetch was in flight.
      setIsCompanyInfoLoaded(true);
      const isStaleNode = rappidStore.activeElement !== targetElement;
      const isBullhornProvider = atsStore.providerName === Enum.AtsProvider.Bullhorn;

      const initialOptions = isBullhornProvider
        ? optionsUpdateRecord.filter(item => item.value !== Enum.UpdateRecordOwnerType.NoUser && item.value !== Enum.UpdateRecordOwnerType.PlacementRecruiter)
        : optionsUpdateRecord;
      if (!isStaleNode) setUpdateRecordOption(initialOptions);

      const cloneUpdateRecordForm = isStaleNode ? { ...initialFormUpdateRecord } : { ...automationStore.formUpdateRecord };
      if (isEditingExistingUpdateRecord && isCopiedTemplate === true && typeof cloneUpdateRecordForm.isUpdatedFromCopiedTemplate === "undefined") {
        switch (cloneUpdateRecordForm.recordField) {
          case Enum.UpdateRecordList.CurrentWorkType:
            cloneUpdateRecordForm.workTypeId = "";
            cloneUpdateRecordForm.workType = "";
            break;
          case Enum.UpdateRecordList.Status:
          case Enum.UpdateRecordList.ApplicationStatus:
            cloneUpdateRecordForm.status = "";
            cloneUpdateRecordForm.statusId = "";
            break;
          case Enum.UpdateRecordList.PlacementStatus:
            cloneUpdateRecordForm.placementStatusId = "";
            cloneUpdateRecordForm.placementStatus = "";
            break;
          case Enum.UpdateRecordList.JobStatus:
            cloneUpdateRecordForm.jobStatusId = "";
            cloneUpdateRecordForm.jobStatus = "";
            break;
          case Enum.UpdateRecordList.Rating:
            cloneUpdateRecordForm.rating = "";
            break;
          case Enum.UpdateRecordList.AddNote:
            cloneUpdateRecordForm.note = "";
            cloneUpdateRecordForm.noteStatus = "";
            cloneUpdateRecordForm.isUpdateAdditionalNotes = true;
            cloneUpdateRecordForm.additionalNoteType = Enum.UpdateRecordNoteType.CandidateNote;
            break;
          case Enum.UpdateRecordList.CustomFields:
          case Enum.UpdateRecordList.PlacementCustomFields:
          case Enum.UpdateRecordList.ApplicationCustomFields:
          case Enum.UpdateRecordList.JobCustomFields:
            cloneUpdateRecordForm.fieldId = "";
            cloneUpdateRecordForm.actionId = "";
            cloneUpdateRecordForm.values = [];
            break;
          case Enum.UpdateRecordList.CandidateSkills:
            cloneUpdateRecordForm.actionId = "";
            cloneUpdateRecordForm.skills = [];
            cloneUpdateRecordForm.keys = null;
            cloneUpdateRecordForm.names = "";
            break;
          default:
            break;
        }

        if (!isStaleNode) automationStore.setFormUpdateRecord(cloneUpdateRecordForm);
        EditRappidNode(RappidNodeType.UpdateRecord, cloneUpdateRecordForm.name, "", "", "", cloneUpdateRecordForm, false, true, targetElement);
      }

      if (isStaleNode) return;

      // Initialize customFieldsOptions for existing custom field update records
      const currentForm = automationStore.formUpdateRecord;
      if (isCustomFieldsRecordField(currentForm.recordField) && currentForm.fieldId && !currentForm.customFieldsOptions) {
        const cfOptions = getCustomFieldsForTriggerType();
        const parsedOptions = cfOptions?.map(field => ({ ...field, values: JSON.parse(field?.values) }));
        const isRemoveAction = currentForm.actionId === CustomFieldsAction.Remove;
        const filteredOptions = isRemoveAction ? parsedOptions?.filter(x => !x.isMandatory) : parsedOptions;
        const selectedField = findCustomField(filteredOptions, currentForm.fieldId, currentForm.recordField);
        const isDateFieldInit = selectedField?.type?.toLowerCase() === "date";
        const optionValues = isDateFieldInit ? [] : (selectedField?.values || []).map(value => ({ label: value, value }));

        // For backward compatibility: existing date field automations without dateMode default to SpecificDate
        const initDateMode = isDateFieldInit && currentForm.actionId === CustomFieldsAction.Add
          ? (currentForm.dateMode || CustomFieldDateMode.SpecificDate)
          : currentForm.dateMode || null;

        automationStore.setFormUpdateRecord({
          ...currentForm,
          customFieldsOptions: filteredOptions,
          customFieldsOptionValues: optionValues,
          isDateField: isDateFieldInit,
          isMultiSelect: selectedField?.isMultiSelect || false,
          dateMode: initDateMode,
        }, true);
      }

      if (!isPlacementOrJobBasedTrigger || triggerList.length === 0) {
        const filters = new Set([UpdateRecordOwnerType.JobOwner, UpdateRecordOwnerType.PlacementRecruiter]);

        // convert to
        const ownerType = formUpdateRecord.ownerType === UpdateRecordOwnerType.PlacementRecruiter || formUpdateRecord.ownerType === UpdateRecordOwnerType.JobOwner
            ? ownerDefaultValue.value
            : formUpdateRecord.ownerType;

        // convert the additional note to candidate note
        const additionalNoteType = automationStore.formUpdateRecord.additionalNoteType !== Enum.UpdateRecordNoteType.CandidateNote
          ? UpdateRecordNoteType.CandidateNote
          : formUpdateRecord.additionalNoteType;

        automationStore.setFormUpdateRecord({
          ...automationStore.formUpdateRecord,
          additionalNoteType: additionalNoteType,
          ownerType: ownerType
        }, true);

        setUpdateRecordOption(getFilteredOptions(optionsUpdateRecord, Array.from(filters)));
      }

      if (formUpdateRecord.isFromOwner === false || formUpdateRecord.ownerType === null) {
        automationStore.setFormUpdateRecord({
          ...automationStore.formUpdateRecord,
          isFromOwner: true,
          ownerType: Enum.UpdateRecordOwnerType.AdminUser
        });
      }

      if (isPlacementOrJobBasedTrigger) {
        const filters = new Set();

        if (triggerType !== TriggerType.PlacementBased) {
          filters.add(UpdateRecordOwnerType.PlacementRecruiter);

          const ownerType = formUpdateRecord.ownerType === UpdateRecordOwnerType.PlacementRecruiter
            ? ownerDefaultValue.value
            : formUpdateRecord.ownerType;

          const additionalNoteType = formUpdateRecord.additionalNoteType === Enum.UpdateRecordNoteType.PlacementNote
            ? Enum.UpdateRecordNoteType.JobNote
            : formUpdateRecord.additionalNoteType;

          automationStore.setFormUpdateRecord({
            ...automationStore.formUpdateRecord,
            ownerType: ownerType,
            isUpdateAdditionalNotes: true,
            additionalNoteType: additionalNoteType
          }, true);
        }

        if (automationStore.formUpdateRecord.additionalNoteType ?? false !== Enum.UpdateRecordNoteType.CandidateNote) {
          filters.add(UpdateRecordOwnerType.NoUser);
        }

        setUpdateRecordOption(getFilteredOptions(optionsUpdateRecord, Array.from(filters)));
      }

      // Normalise Note steps last, so a draft saves what the pickers show (ROI-351). Read the store again:
      // the blocks above write ownerType back from the stale formUpdateRecord closure.
      const current = automationStore.formUpdateRecord;
      if (!isStaleNode && current.recordField === Enum.UpdateRecordList.AddNote) {
        if (current.ownerType == null) {
          automationStore.setFormUpdateRecord({ ...current, isFromOwner: true, ownerType: Enum.UpdateRecordOwnerType.AdminUser }, true);
        } else if (isBullhornProvider && current.ownerType === Enum.UpdateRecordOwnerType.NoUser) {
          // Bullhorn does not offer "No user"; a new step may have defaulted to it before the provider was known
          automationStore.setFormUpdateRecord({ ...current, isFromOwner: true, ownerType: Enum.UpdateRecordOwnerType.AdminUser }, true);
        } else if (current.isFromOwner == null) {
          // an owner was picked before ROI-351 and the flag was never written
          automationStore.setFormUpdateRecord({ ...current, isFromOwner: true }, true);
        }
      }
    });

    return function cleanup() {
      automationStore.resetFormUpdateRecord();
    }
    // eslint-disable-next-line
  }, []);

  // Placement/Job/Application custom fields are only relevant on Placement- or Job-based automations —
  // fetch them here instead of unconditionally on every canvas mount.
  useEffect(() => {
    if (triggerType === Enum.TriggerType.PlacementBased) {
      lookupStore.getPlacementCustomFields();
      lookupStore.getJobCustomFields();
    } else if (triggerType === Enum.TriggerType.JobBased) {
      lookupStore.getJobCustomFields();
      if (automationType === Enum.AutomationType.Candidate) {
        lookupStore.getApplicationCustomFields();
      }
    }
  }, [triggerType, automationType]);

  const isShowAdditionalNotes = () => !isJobScopeDisabled && !isUseLegacyAutomation && isShowJobAdditionalNotes();

  const getFilteredOptions = (baseOptions, additionalFilters = []) => {
    const isBullhornProvider = atsStore.providerName === Enum.AtsProvider.Bullhorn;
    const filters = [...additionalFilters];

    // Hide "No user" and "Placement recruiter" options when for Bullhorn provider
    if (isBullhornProvider) {
      filters.push(Enum.UpdateRecordOwnerType.NoUser);
      filters.push(Enum.UpdateRecordOwnerType.PlacementRecruiter);
    }

    return baseOptions.filter(item => !filters.includes(item.value));
  }

  const getDefaultOwnerType = (isBullhornProvider) => {
    // When Bullhorn provider is active, use "Admin user" instead of "No user"
    const defaultIndex = isBullhornProvider ? 2 : 3;
    return automationStore.automationType === Enum.AutomationType.Candidate
      ? Enum.OwnerTypeCandidate[defaultIndex].value
      : Enum.OwnerTypeClient[defaultIndex].value;
  }

  console.debug("Update Record Form Data: ", toJS(formUpdateRecord));
  const isEmptyCandidateNoteStatus = lookupStore.candidateNoteStatuses.length === 0;
  const isEmptyClientNoteStatus = lookupStore.clientNoteStatuses.length === 0;
  const isEmptyNoteStatus = automationType === Enum.AutomationType.Candidate ? isEmptyCandidateNoteStatus : isEmptyClientNoteStatus;

  const isDisableButton = () => {
    switch (formUpdateRecord.recordField) {
      case Enum.UpdateRecordList.Postcode:
        return formUpdateRecord.postcode ? false : true;
      case Enum.UpdateRecordList.CurrentWorkType:
        return formUpdateRecord.workType ? false : true;
      case Enum.UpdateRecordList.Country:
        return formUpdateRecord.country ? false : true;
      case Enum.UpdateRecordList.Status:
        return formUpdateRecord.status ? false : true;
      case Enum.UpdateRecordList.AddNote:
        return formUpdateRecord.noteStatus && formUpdateRecord.note && !isEmptyNoteStatus ? false : true;
      case Enum.UpdateRecordList.ApplicationStatus:
        return formUpdateRecord.applicationStatus ? false : true;
      case Enum.UpdateRecordList.ApplicationRating: {
        // A copied template can carry this recordField into a Bullhorn company (where it's not offered
        // in the picker) - block submit here too, since the backend rejects it with UnsupportedProvider
        // at run time, not at save time.
        if (atsStore.providerName === Enum.AtsProvider.Bullhorn) return true;
        const rating = formUpdateRecord.applicationRating;
        return rating === undefined || rating === null || rating < 0 || rating > 10;
      }
      case Enum.UpdateRecordList.CustomFields:
      case Enum.UpdateRecordList.PlacementCustomFields:
      case Enum.UpdateRecordList.ApplicationCustomFields:
      case Enum.UpdateRecordList.JobCustomFields:
        if (!formUpdateRecord.actionId || !formUpdateRecord.fieldId) return true;
        // Date fields and single-select fields with Remove action don't require values
        if (formUpdateRecord.actionId === CustomFieldsAction.Remove && (formUpdateRecord.isDateField || !formUpdateRecord.isMultiSelect)) return false;
        // Date field with Add action requires dateMode selection
        if (formUpdateRecord.isDateField && formUpdateRecord.actionId === CustomFieldsAction.Add) {
          if (!formUpdateRecord.dateMode) return true;
          // Event's date doesn't require a value
          if (formUpdateRecord.dateMode === CustomFieldDateMode.EventDate) return false;
          // Specific date requires a value
          return formUpdateRecord.values?.length <= 0;
        }
        return formUpdateRecord.values?.length <= 0;
      case Enum.UpdateRecordList.CandidateSkills:
        return formUpdateRecord.skills?.length <= 0
      case Enum.UpdateRecordList.Unsubscribe:
        return ![Enum.UnsubscribeSubType.Provider, Enum.UnsubscribeSubType.RoiAi].includes(formUpdateRecord.unsubscribeValue);
      case Enum.UpdateRecordList.Rating:
        return formUpdateRecord.rating ? false : true;
      case Enum.UpdateRecordList.City:
        return formUpdateRecord.city ? false : true;
      case Enum.UpdateRecordList.State:
        return formUpdateRecord.state ? false : true;
      case Enum.UpdateRecordList.CandidatePostalCode:
        return formUpdateRecord.postcode ? false : true;
      case Enum.UpdateRecordList.CandidateCountry:
        return formUpdateRecord.country ? false : true;
      case Enum.UpdateRecordList.PlacementStatus:
        return formUpdateRecord.placementStatus ? false : true;
      case Enum.UpdateRecordList.JobStatus:
        return formUpdateRecord.jobStatus ? false : true;
      default:
        return true;
    }
  };

  // Handle when custom fields action (add/remove) selected
  const handleCustomFieldsAction = (action) => {
    if (formUpdateRecord.actionId === action.id) return;

    // when remove action selected, remove mandatory from option
    const isRemoveAction = action.id === CustomFieldsAction.Remove;
    const newOptions = isRemoveAction
      ? parseOptionCustomFields?.filter(x => !x.isMandatory)
      : parseOptionCustomFields;

    // when action changes, reset all setup
    automationStore.setFormUpdateRecord({
      ...formUpdateRecord,
      customFieldsOptions: newOptions,
      actionId: action.id,
      fieldId: 0,
      isMultiSelect: false,
      dateMode: null,
      values: []
    });
  };

  // Handle when skills action (add/remove) selected
  const handleSkillsAction = (action) => {
    if (formUpdateRecord.actionId === action.id) return;

    automationStore.setFormUpdateRecord({
      ...formUpdateRecord,
      actionId: action.id,
      isMultiSelect: false,
      skills: [],
      names: ""
    });
  };

  const handleCustomFieldsSelected = (field) => {
    // `field` is already the exact selected option object (react-select passes the option directly) —
    // don't re-look-up by fieldId alone, since Job/Application custom fields are merged into one list and
    // can share the same fieldId across their independent datasets.
    const isDateField = field?.type?.toLowerCase() === "date";
    const values = isDateField ? [] : (field?.values || []).map(value => ({ label: value, value }));

    automationStore.setFormUpdateRecord({
      ...formUpdateRecord,
      recordField: field.updateRecordType || Enum.UpdateRecordList.CustomFields,
      customFieldsOptionValues: values,
      fieldId: field.fieldId,
      isMultiSelect: field.isMultiSelect || false,
      isDateField: isDateField,
      dateMode: null,
      values: []
    });
  };

  const handleCustomFieldsValueSelected = (selected) => {
    const values = Array.isArray(selected)
      ? selected.map(item => item?.value)
      : selected
        ? [selected.value]
        : [];

    automationStore.setFormUpdateRecord({ ...formUpdateRecord, values });
  };

  const handleCustomFieldDateChange = (e) => {
    const value = e.target.value;
    if (value) {
      automationStore.setFormUpdateRecord({ ...formUpdateRecord, values: [value] });
    } else {
      automationStore.setFormUpdateRecord({ ...formUpdateRecord, values: [] });
    }
  };

  const handleCustomFieldDateModeChange = (mode) => {
    automationStore.setFormUpdateRecord({
      ...formUpdateRecord,
      dateMode: mode.id,
      values: []
    });
  };

  const selectedAction = CandidateSkillsActions.find(
    (x) => x.id === formUpdateRecord.actionId
  );

  const selectedCustomField = findCustomField(formUpdateRecord.customFieldsOptions, formUpdateRecord.fieldId, formUpdateRecord.recordField);
  const isDateField = formUpdateRecord.isDateField || selectedCustomField?.type?.toLowerCase() === "date";

  // Treat the provider as Bullhorn (conservative) until getCompanyInfo() confirms otherwise, so
  // JobAdder-only options never render as selectable before we actually know the provider.
  const updateFieldGroups = buildUpdateFieldGroups(updateRecordOptions, automationType, triggerType, !isCompanyInfoLoaded || atsStore.providerName === Enum.AtsProvider.Bullhorn);

  return (
    <>
      <HeaderForm
        automationHeaderId="form-step-updaterecord"
        automationHeaderImgSrc={stepUpdateRecordIcon}
        automationHeaderImgAlt="icon-updaterecord"
        automationHeaderTextStep={formUpdateRecord.recordField === Enum.UpdateRecordList.AddNote ? "Add Note" : "Field"}
      />
      {formUpdateRecord.recordField !== Enum.UpdateRecordList.AddNote && (
      <FormGroup className="step-body-text">
        <span className="mb-3"><b><Label className="text-white">Choose field</Label></b></span>
        <GroupedPicker
          groups={updateFieldGroups}
          selectedLabel={
            // Job/Application Custom Fields have their own distinct entries (and names) in updateRecordOptions;
            // Placement/Candidate/Client custom fields don't (they're all tagged with a single shared entry),
            // so fall back to that shared "Custom Fields" label when there's no exact match.
            updateRecordOptions.find(x => x.id === formUpdateRecord.recordField && x.id !== Enum.UpdateRecordList.AddNote)?.name
            || (isCustomFieldsRecordField(formUpdateRecord.recordField)
              ? updateRecordOptions.find(x => x.id === Enum.UpdateRecordList.CustomFields)?.name
              : null)
            || null
          }
          placeholder="- Select -"
          isDisabled={!isDraft}
          onSelect={e => {
            let data = {};
            data.recordField = e.id;
            data.name = e.name;
            const isBullhornProvider = atsStore.providerName === Enum.AtsProvider.Bullhorn;
            if (e.id === Enum.UpdateRecordList.AddNote) {
              data.isFromOwner = true;
              data.ownerType = getDefaultOwnerType(isBullhornProvider);
              if (triggerList.length > 0 && isPlacementOrJobBasedTrigger) {
                data.ownerType = automationStore.automationType === Enum.AutomationType.Candidate ? Enum.OwnerTypeCandidate[1].value : Enum.OwnerTypeClient[1].value
              }
              if (triggerList.length > 0 && isShowAdditionalNotes) {
                data.isUpdateAdditionalNotes = true;
                data.additionalNoteType = isPlacementOrJobBasedTrigger ? (isPlacementBasedTrigger ? Enum.UpdateRecordNoteType.PlacementNote : Enum.UpdateRecordNoteType.JobNote) : Enum.UpdateRecordNoteType.CandidateNote;
                data.ownerType = getDefaultOwnerType(isBullhornProvider);

                if (isPlacementOrJobBasedTrigger) {
                  data.ownerType = automationStore.automationType === Enum.AutomationType.Candidate ? Enum.OwnerTypeCandidate[1].value : Enum.OwnerTypeClient[1].value;
                  setUpdateRecordOption(getFilteredOptions(optionsUpdateRecord, [Enum.UpdateRecordOwnerType.NoUser]))
                }
              }
            } else if (e.id === Enum.UpdateRecordList.Unsubscribe) {
              data.unsubscribeValue = null;
            } else if (!!formUpdateRecord.isFromOwner || !!formUpdateRecord.ownerType) {
              data.isFromOwner = false;
              data.ownerType = null;
            }
            automationStore.setFormUpdateRecord(data);
          }}
        />
      </FormGroup>
      )}

      {formUpdateRecord.recordField === Enum.UpdateRecordList.Postcode &&
        <FormGroup className="step-body-text">
          <Input
            type="number"
            id="automation-updaterecord-postcode"
            className="rounded-pill automation-alert-input"
            disabled={!isDraft}
            onChange={e => {
              let data = { ...formUpdateRecord };
              data.postcode = e.target.value;
              automationStore.setFormUpdateRecord(data);
            }}
            value={String(formUpdateRecord.postcode)}
          />
        </FormGroup>
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.CurrentWorkType &&
        <Select
          id="automation-updaterecord-worktype"
          classNamePrefix="automation-dropdown"
          placeholder="- Select -"
          options={currentWorkTypes}
          isDisabled={!isDraft}
          onChange={e => {
            let data = { ...formUpdateRecord };
            data.workTypeId = e.workTypeId;
            data.workType = e.name;
            automationStore.setFormUpdateRecord(data);
          }}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.workTypeId}
          value={currentWorkTypes.find(x => x.workTypeId === formUpdateRecord.workTypeId) || 0}
        />
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.Country &&
        <Select
          id="automation-updaterecord-country"
          classNamePrefix="automation-dropdown"
          placeholder="- Select -"
          options={listCountry}
          isDisabled={!isDraft}
          onChange={e => {
            let data = { ...formUpdateRecord };
            data.country = e.name;
            data.countryCode = e.code;
            automationStore.setFormUpdateRecord(data);
          }}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.name}
          value={listCountry.find(x => x.name === formUpdateRecord.country) || ""}
        />
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.Rating &&
        <Select
          id="automation-updaterecord-rating"
          classNamePrefix="automation-dropdown"
          placeholder="- Select -"
          options={ratings}
          isDisabled={!isDraft}
          onChange={e => {
            let data = { ...formUpdateRecord };
            data.rating = e.name;
            automationStore.setFormUpdateRecord(data);
          }}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.name}
          value={ratings.find(x => x.name === formUpdateRecord.rating) || null}
        />
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.City &&
        <FormGroup className="step-body-text">
          <Input
            type="text"
            id="automation-updaterecord-city"
            className="rounded-pill automation-alert-input"
            disabled={!isDraft}
            onChange={e => {
              let data = { ...formUpdateRecord };
              data.city = e.target.value;
              automationStore.setFormUpdateRecord(data);
            }}
            value={formUpdateRecord.city || ""}
          />
        </FormGroup>
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.State &&
        <FormGroup className="step-body-text">
          <Input
            type="text"
            id="automation-updaterecord-state"
            className="rounded-pill automation-alert-input"
            disabled={!isDraft}
            onChange={e => {
              let data = { ...formUpdateRecord };
              data.state = e.target.value;
              automationStore.setFormUpdateRecord(data);
            }}
            value={formUpdateRecord.state || ""}
          />
        </FormGroup>
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.CandidatePostalCode &&
        <FormGroup className="step-body-text">
          <Input
            type="text"
            id="automation-updaterecord-candidate-postcode"
            className="rounded-pill automation-alert-input"
            disabled={!isDraft}
            onChange={e => {
              let data = { ...formUpdateRecord };
              data.postcode = e.target.value;
              automationStore.setFormUpdateRecord(data);
            }}
            value={formUpdateRecord.postcode || ""}
          />
        </FormGroup>
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.CandidateCountry &&
        // Uses the ATS-synced country list (lookupStore.countries), not the static assets/json/countries
        // list used by the legacy Country field below — the backend resolves countryCode against the
        // synced list and fails the step if it isn't found there.
        <Select
          id="automation-updaterecord-candidate-country"
          classNamePrefix="automation-dropdown"
          placeholder="- Select -"
          options={countries}
          isDisabled={!isDraft || countries.length === 0}
          onChange={e => {
            let data = { ...formUpdateRecord };
            data.country = e.name;
            data.countryCode = e.code;
            automationStore.setFormUpdateRecord(data);
          }}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.name}
          value={countries.find(x => x.name === formUpdateRecord.country) || null}
        />
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.PlacementStatus &&
        <Select
          id="automation-updaterecord-placement-status"
          classNamePrefix="automation-dropdown"
          placeholder="- Select -"
          options={placementStatusOptions}
          isDisabled={!isDraft || placementStatusOptions.length === 0}
          onChange={e => {
            let data = { ...formUpdateRecord };
            data.placementStatusId = e.statusId;
            data.placementStatus = e.name;
            automationStore.setFormUpdateRecord(data);
          }}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.statusId}
          value={placementStatusOptions.find(x => x.statusId === formUpdateRecord.placementStatusId) || null}
        />
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.JobStatus &&
        <Select
          id="automation-updaterecord-job-status"
          classNamePrefix="automation-dropdown"
          placeholder="- Select -"
          options={jobStatusOptions}
          isDisabled={!isDraft || jobStatusOptions.length === 0}
          onChange={e => {
            let data = { ...formUpdateRecord };
            data.jobStatusId = e.statusId;
            data.jobStatus = e.name;
            automationStore.setFormUpdateRecord(data);
          }}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.statusId}
          value={jobStatusOptions.find(x => x.statusId === formUpdateRecord.jobStatusId) || null}
        />
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.Status &&
        <Select
          id="automation-updaterecord-status"
          classNamePrefix="automation-dropdown"
          placeholder="- Select -"
          options={statuses}
          isDisabled={!isDraft}
          onChange={e => {
            let data = { ...formUpdateRecord };
            data.statusId = e.statusId;
            data.status = e.name;
            automationStore.setFormUpdateRecord(data);
          }}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.statusId}
          value={statuses.find(x => x.statusId === formUpdateRecord.statusId) || 0}
        />
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.ApplicationRating &&
        <Select
          id="automation-updaterecord-application-rating"
          classNamePrefix="automation-dropdown"
          placeholder="- Select -"
          options={applicationRatingOptions}
          isDisabled={!isDraft}
          onChange={e => {
            automationStore.setFormUpdateRecord({ ...formUpdateRecord, applicationRating: e.value });
          }}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          value={applicationRatingOptions.find(x => x.value === formUpdateRecord.applicationRating) ?? null}
        />
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.AddNote &&
        <>
          <Select
            id="automation-updaterecord-notetype"
            classNamePrefix="automation-dropdown"
            placeholder="- Select note type -"
            options={noteStatuses}
            isDisabled={!isDraft}
            onChange={e => {
              let data = { ...formUpdateRecord };
              data.noteStatus = e.name;
              automationStore.setFormUpdateRecord(data);
            }}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.name}
            value={noteStatuses.find(x => x.name === formUpdateRecord.noteStatus) || ""}
          />
          <FormGroup className="step-body-text">
            <Input
              type="textarea"
              className="automation-form-dropdown-multi textarea-form"
              id="automation-updaterecord-note-text"
              disabled={!isDraft}
              onChange={e => {
                let data = { ...formUpdateRecord };
                data.note = e.target.value;
                automationStore.setFormUpdateRecord(data);
              }}
              value={formUpdateRecord.note}
            />
          </FormGroup>
          <FormGroup className="step-body-text">
            <Label check htmlFor={`automation-updaterecord-from-owner`} className="mb-2">
              Add note from
            </Label>
            {triggerList.length > 0 && isPlacementOrJobBasedTrigger ? (
              <Select
                id="automation-updaterecord-from-ownertype"
                className={`automation-email-template col-md-12 px-0 mb-2 ${!formUpdateRecord.isFromOwner && `automation-disable-dropdown`}`}
                classNamePrefix="automation-dropdown"
                placeholder="Select Owner"
                menuPosition="fixed"
                menuPlacement="auto"
                menuShouldBlockScroll={true}
                isDisabled={!isDraft}
                onChange={(e) => {
                  automationStore.setFormUpdateRecord({ ...formUpdateRecord, isFromOwner: true, ownerType: e.value });
                }}
                options={updateRecordOption}
                getOptionLabel={(option) => option?.label}
                getOptionValue={(option) => option?.value}
                value={
                  formUpdateRecord.ownerType
                    ? optionsUpdateRecord.find(x => x.value === formUpdateRecord.ownerType)
                    : ownerUnsetValue
                }
              />)
              : (<Select
                id="automation-updaterecord-from-ownertype"
                className={`automation-email-template col-md-12 px-0 mb-2 ${!formUpdateRecord.isFromOwner && `automation-disable-dropdown`}`}
                classNamePrefix="automation-dropdown"
                placeholder="Select Owner"
                menuPosition="fixed"
                menuPlacement="auto"
                menuShouldBlockScroll={true}
                isDisabled={!isDraft}
                onChange={(e) => {
                  automationStore.setFormUpdateRecord({ ...formUpdateRecord, isFromOwner: true, ownerType: e.value });
                }}
                options={getFilteredOptions(updateRecordOption, [Enum.UpdateRecordOwnerType.JobOwner])}
                getOptionLabel={(option) => option?.label}
                getOptionValue={(option) => option?.value}
                value={
                  formUpdateRecord.ownerType
                    ? optionsUpdateRecord.find(x => x.value === formUpdateRecord.ownerType)
                    : ownerUnsetValue
                }
              />)
            }
          </FormGroup>
          {isShowAdditionalNotes() &&
            <FormGroup className="step-body-text">
              <span className="mb-3"><b><Label className="text-white">Add note to</Label></b></span><br />
              <Input
                type="checkbox"
                id="automation-update-to-candidate-note"
                className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
                checked={formUpdateRecord.isUpdateAdditionalNotes && formUpdateRecord.additionalNoteType === Enum.UpdateRecordNoteType.CandidateNote}
                disabled={!isDraft}
                onChange={(e) => {
                  automationStore.setFormUpdateRecord({
                    ...formUpdateRecord,
                    isUpdateAdditionalNotes: e.target.checked,
                    additionalNoteType: Enum.UpdateRecordNoteType.CandidateNote
                  });
                  setUpdateRecordOption(getFilteredOptions(optionsUpdateRecord))
                }}
              />
              <Label check htmlFor={`automation-update-to-candidate-note`} className="mb-1 mr-3">
                {automationType === Enum.AutomationType.Candidate ? 'Candidate' : 'Contact'}
              </Label>
              {isShowPlacementAdditionalNotes() &&
                <>
                  <Input
                    type="checkbox"
                    id="automation-update-to-placement-note"
                    className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
                    checked={formUpdateRecord.isUpdateAdditionalNotes && formUpdateRecord.additionalNoteType === Enum.UpdateRecordNoteType.PlacementNote}
                    disabled={!isDraft}
                    onChange={(e) => {
                      automationStore.setFormUpdateRecord({
                        ...formUpdateRecord,
                        isUpdateAdditionalNotes: e.target.checked,
                        additionalNoteType: Enum.UpdateRecordNoteType.PlacementNote,
                        ownerType: formUpdateRecord.isFromOwner ? (formUpdateRecord.ownerType === Enum.UpdateRecordOwnerType.NoUser ? Enum.UpdateRecordOwnerType.JobOwner : formUpdateRecord.ownerType) : null
                      });
                      setUpdateRecordOption(getFilteredOptions(optionsUpdateRecord, [Enum.UpdateRecordOwnerType.NoUser]))
                    }}
                  />
                  <Label check htmlFor={`automation-update-to-placement-note`} className="mb-1 mr-3">
                    Placement
                  </Label>
                </>
              }
              <Input
                type="checkbox"
                id="automation-update-to-job-note"
                className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
                checked={formUpdateRecord.isUpdateAdditionalNotes && formUpdateRecord.additionalNoteType === Enum.UpdateRecordNoteType.JobNote}
                disabled={!isDraft}
                onChange={(e) => {
                  automationStore.setFormUpdateRecord({
                    ...formUpdateRecord,
                    isUpdateAdditionalNotes: e.target.checked,
                    additionalNoteType: Enum.UpdateRecordNoteType.JobNote,
                    ownerType: formUpdateRecord.isFromOwner ? (formUpdateRecord.ownerType === Enum.UpdateRecordOwnerType.NoUser ? Enum.UpdateRecordOwnerType.JobOwner : formUpdateRecord.ownerType) : null
                  });
                  setUpdateRecordOption(getFilteredOptions(optionsUpdateRecord, [Enum.UpdateRecordOwnerType.NoUser]))
                }}
              />
              <Label check htmlFor={`automation-update-to-job-note`} className="mb-1 mr-3">
                Job
              </Label>
              {isShowApplicationAdditionalNotes() &&
                <>
                  <Input
                    type="checkbox"
                    id="automation-update-to-application-note"
                    className="automation-time-variable-checkbox-days custom-checkbox-pull-left"
                    checked={formUpdateRecord.isUpdateAdditionalNotes && formUpdateRecord.additionalNoteType === Enum.UpdateRecordNoteType.ApplicationNote}
                    disabled={!isDraft}
                    onChange={(e) => {
                      automationStore.setFormUpdateRecord({
                        ...formUpdateRecord,
                        isUpdateAdditionalNotes: e.target.checked,
                        additionalNoteType: Enum.UpdateRecordNoteType.ApplicationNote,
                        ownerType: formUpdateRecord.isFromOwner ? (formUpdateRecord.ownerType === Enum.UpdateRecordOwnerType.NoUser ? Enum.UpdateRecordOwnerType.JobOwner : formUpdateRecord.ownerType) : null
                      });
                      setUpdateRecordOption(getFilteredOptions(optionsUpdateRecord, [Enum.UpdateRecordOwnerType.NoUser]))
                    }}
                  />
                  <Label check htmlFor={`automation-update-to-application-note`} className="mb-1 mr-3">
                    Application
                  </Label>
                </>
              }
            </FormGroup>
          }
        </>
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.ApplicationStatus &&
        <StyledMultiSelectWithSearch
          showSelectAll={false}
          valueKey="name"
          labelKey="name"
          showTags={true}
          hideAnyOption={true}
          className="automation-form-dropdown-multi"
          id="automation-updaterecord-application-status"
          name="automation-updaterecord-application-status"
          classNamePrefix="automation-dropdown"
          placeholder="- Select -"
          isDisabled={!isDraft || applicationStatusOptions.length === 0}
          options={applicationStatusOptions}
          value={
            applicationStatusOptions.filter(
              x => x.statusId === formUpdateRecord.applicationStatusId
            ) || []
          }
          onChange={e => {
            e = e || [];
            let data = { ...formUpdateRecord };
            let selected = null;

            if (e.length === 1) {
              selected = e[0];
            } else if (e.length > 1) {
              selected =
                e.find(
                  x => x.statusId !== formUpdateRecord.applicationStatusId
                ) || e[e.length - 1];
            }

            data.applicationStatusId = selected?.statusId ?? "";
            data.applicationStatus = selected?.name ?? "";
            automationStore.setFormUpdateRecord(data);
          }}
        />
      }

      {isCustomFieldsRecordField(formUpdateRecord.recordField) &&
        <>
          <FormGroup className="step-body-text">
            <Select
              id="automation-updaterecord-customfields-action"
              classNamePrefix="automation-dropdown"
              placeholder="- Select Action -"
              options={CustomFieldsActions}
              onChange={handleCustomFieldsAction}
              isDisabled={!isDraft}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              value={selectedAction || null}
            />
          </FormGroup>
          <FormGroup className="step-body-text">
            <Select
              id="automation-updaterecord-customfields"
              classNamePrefix="automation-dropdown"
              placeholder="- Select Custom Fields -"
              options={formUpdateRecord.customFieldsOptions}
              onChange={handleCustomFieldsSelected}
              isDisabled={!isDraft}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => `${option.updateRecordType}-${option.fieldId}`}
              value={findCustomField(formUpdateRecord.customFieldsOptions, formUpdateRecord.fieldId, formUpdateRecord.recordField)}
            />
          </FormGroup>
          {formUpdateRecord.fieldId > 0 && isDateField && formUpdateRecord.actionId === CustomFieldsAction.Add &&
            <FormGroup className="step-body-text">
              <Select
                id="automation-updaterecord-customfields-datemode"
                classNamePrefix="automation-dropdown"
                placeholder="- Select Date Mode -"
                options={CustomFieldDateModes}
                onChange={handleCustomFieldDateModeChange}
                isDisabled={!isDraft}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                value={CustomFieldDateModes.find(x => x.id === formUpdateRecord.dateMode) || null}
              />
            </FormGroup>
          }
          {formUpdateRecord.fieldId > 0 && isDateField && formUpdateRecord.actionId === CustomFieldsAction.Add && formUpdateRecord.dateMode === CustomFieldDateMode.SpecificDate &&
            <FormGroup className="step-body-text">
              <Input
                type="date"
                className="form-control automation-date-input"
                onChange={handleCustomFieldDateChange}
                value={formUpdateRecord.values?.[0] || ''}
                disabled={!isDraft}
              />
            </FormGroup>
          }
          {formUpdateRecord.fieldId > 0 && !isDateField && (formUpdateRecord.actionId === CustomFieldsAction.Add || formUpdateRecord.actionId === CustomFieldsAction.Remove) &&
            !(formUpdateRecord.actionId === CustomFieldsAction.Remove && !formUpdateRecord.isMultiSelect) &&
            <FormGroup className="step-body-text">
              <Select
                isMulti={formUpdateRecord.isMultiSelect}
                id="automation-updaterecord-customfields-value"
                classNamePrefix="automation-dropdown"
                placeholder="- Select -"
                options={formUpdateRecord.customFieldsOptionValues}
                onChange={handleCustomFieldsValueSelected}
                isDisabled={!isDraft}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                value={formUpdateRecord.customFieldsOptionValues?.filter(x => formUpdateRecord.values?.includes(x.value))}
              />
            </FormGroup>
          }
        </>
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.CandidateSkills &&
        <>
          <FormGroup className="step-body-text">
            <Select
              id="automation-updaterecord-skills-action"
              classNamePrefix="automation-dropdown"
              placeholder="- Select Action -"
              options={CandidateSkillsActions}
              onChange={handleSkillsAction}
              isDisabled={!isDraft}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              value={selectedAction || null}
            />
          </FormGroup>
          <FormGroup className="step-body-text">
            <CandidateSkillsSelector
              keys={formUpdateRecord.keys || []}
              skills={formUpdateRecord.skills || []}
              form={formUpdateRecord}
              automationStore={automationStore}
              candidateSkillsHierarchy={candidateSkillsHierarchy}
              formType="updaterecord"
            />
            {formUpdateRecord.actionId === CustomFieldsAction.Remove && (
              <small className="text-muted">
                Removing a category or subcategory removes every skill under it on the record.
              </small>
            )}
          </FormGroup>
        </>
      }

      {formUpdateRecord.recordField === Enum.UpdateRecordList.Unsubscribe &&
        <FormGroup className="step-body-text">
          <Select
            id="automation-updaterecord-unsubscribe-value"
            classNamePrefix="automation-dropdown"
            placeholder="- Select -"
            options={unsubscribeOptions}
            isDisabled={!isDraft}
            onChange={e => {
              automationStore.setFormUpdateRecord({
                ...formUpdateRecord,
                unsubscribeValue: e.id
              });
            }}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            value={unsubscribeOptions.find(x => x.id === formUpdateRecord.unsubscribeValue) || null}
          />
        </FormGroup>
      }

      {isDraft &&
        <SubmitButton
          type={RappidNodeType.UpdateRecord}
          text={setUpdateRecordNodeTextLine1(formUpdateRecord)}
          text2={setUpdateRecordNodeTextLine2(formUpdateRecord)}
          roiData={formUpdateRecord}
          validateConditions={() => validateNodeAndResetBorder()}
          isDisabled={isDisableButton()} />
      }
    </>
  );
});

export const setUpdateRecordNodeTextLine1 = (formUpdateRecord) => {
  switch (formUpdateRecord.recordField) {
    case Enum.UpdateRecordList.Status:
      return "Status updated to: " + formUpdateRecord.status;
    case Enum.UpdateRecordList.AddNote:
      return "Note type: " + formUpdateRecord.noteStatus;
    case Enum.UpdateRecordList.ApplicationStatus:
      return "Application status updated to: " + formUpdateRecord.applicationStatus;
    case Enum.UpdateRecordList.ApplicationRating:
      return "Application rating updated to: " + formatApplicationRating(formUpdateRecord.applicationRating);
    case Enum.UpdateRecordList.CustomFields:
    case Enum.UpdateRecordList.PlacementCustomFields:
    case Enum.UpdateRecordList.ApplicationCustomFields:
    case Enum.UpdateRecordList.JobCustomFields:
      return "Custom fields Action: " + Enum.CustomFieldsActions.find(x => x.id === formUpdateRecord.actionId)?.name;
    case Enum.UpdateRecordList.CandidateSkills:
      return "Skills: " + Enum.CustomFieldsActions.find(x => x.id === formUpdateRecord.actionId)?.name;
    case Enum.UpdateRecordList.Unsubscribe:
      return "Unsubscribed: " + (formUpdateRecord.unsubscribeValue === Enum.UnsubscribeSubType.Provider
        ? getProviderUnsubscribedLabel(atsStore.providerName)
        : "Roi-AI Unsubscribed");
    case Enum.UpdateRecordList.AddToJob:
      return formUpdateRecord.jobTitle ? "Add to job: " + formUpdateRecord.jobTitle : "Add to Job";
    case Enum.UpdateRecordList.UpdateRecruiter:
      return setUpdateRecruiterNodeTextLine1(formUpdateRecord);
    case Enum.UpdateRecordList.Rating:
      return "Rating updated to: " + formUpdateRecord.rating;
    case Enum.UpdateRecordList.City:
      return "City updated to: " + formUpdateRecord.city;
    case Enum.UpdateRecordList.State:
      return "State updated to: " + formUpdateRecord.state;
    case Enum.UpdateRecordList.CandidatePostalCode:
      return "Postcode updated to: " + formUpdateRecord.postcode;
    case Enum.UpdateRecordList.CandidateCountry:
      return "Country updated to: " + formUpdateRecord.country;
    case Enum.UpdateRecordList.PlacementStatus:
      return "Status updated to: " + formUpdateRecord.placementStatus;
    case Enum.UpdateRecordList.JobStatus:
      return "Status updated to: " + formUpdateRecord.jobStatus;
    default:
      return "";
  }
}

export const setUpdateRecordNodeTextLine2 = (formUpdateRecord) => {
  switch (formUpdateRecord.recordField) {
    case Enum.UpdateRecordList.AddNote:
      return "Note added: " + formUpdateRecord.note;
    case Enum.UpdateRecordList.CustomFields:
    case Enum.UpdateRecordList.PlacementCustomFields:
    case Enum.UpdateRecordList.ApplicationCustomFields:
    case Enum.UpdateRecordList.JobCustomFields:
      return "Field updated to: " + formUpdateRecord.customFieldsOptions?.find(x => x.fieldId === formUpdateRecord.fieldId && x.updateRecordType === formUpdateRecord.recordField)?.name;
    case Enum.UpdateRecordList.UpdateRecruiter:
      return setUpdateRecruiterNodeTextLine2(formUpdateRecord);
    default:
      return "";
  }
}

export default UpdateRecordForm;
